import {
	Box,
	Collapse,
	Divider,
	Stack,
	Typography
} from "@mui/material";
import { useContext, useState } from "react";
import ScreenWidth from "../../Helpers/ScreenWidth";
import { TextfieldPicker } from "../Common/TextfieldPicker";

import { isDesktop } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowIcon } from "../../Assets/Icons/arrowSide.svg";
import { ReactComponent as CarIcon } from "../../Assets/Icons/CarIcon.svg";
import { ReactComponent as RestrictionEndIcon } from "../../Assets/Icons/TimeEndIcon.svg";
import { ReactComponent as RestrictionStartIcon } from "../../Assets/Icons/TimeStartIcon.svg";
import { ColorScheme } from "../../Themes/ColorScheme";
import { ApiContext } from "../ApiContext";
import { AlertBar } from "../Common/AlertBar";
import { ConfirmAction } from "../Common/ConfirmAction";
import OnOffSlider from "../Common/OnOffSlider";
import { TextFieldInput } from "../Common/TextFieldInput";
import { WeekdaySelect } from "./WeekdaySelect";

import { ReactComponent as MailIcon } from "../../Assets/Icons/EnvelopeIcon.svg";
import { ReactComponent as PhoneIcon } from "../../Assets/Icons/OldPhoneIcon.svg";
import { ReactComponent as ProfileIcon } from "../../Assets/Icons/UserIcon.svg";
import {
	isValidEmail,
	isValidLicensePlate,
	isValidName
} from "../../Helpers/Validation";
import BigButton from "../Common/BigButton";

function getBlankDate() {
	const date = new Date();
	date.setHours(0, 0, 0, 0);
	return date;
}

// When using as a new door access, data is null but doorId must be provided
export const DoorAccessEdit = ({ data, closeDialog, doorId }) => {
	const { t } = useTranslation();
	const { ApiCall } = useContext(ApiContext);
	const handleWidthCheck = (width) => ScreenWidth(width);

	const [startDate, setStartDate] = useState(data ? new Date(data.access_starts_at) : new Date());
	const [endDate, setEndDate] = useState(data ? new Date(data.access_ends_at) : new Date("2038-01-01T00:00:00Z"));
	const [startTime, setStartTime] = useState(data ? new Date(data.start_time) : getBlankDate());
	const [endTime, setEndTime] = useState(data ? new Date(data.end_time) : getBlankDate());
	const [selectedWeekdays, setSelectedWeekdays] = useState(data ? data.weekdays ?? "" : "0123456");
	const [loading, setLoading] = useState(false);
	const [restrictions, setRestrictions] = useState(data ? data.restrict_times : false);

	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [licensePlate, setLicensePlate] = useState("");
	const [name, setName] = useState("");

	const [error, setError] = useState("");

	const emailValue = email.trim();
	const phoneValue = phone.trim();
	const licensePlateValue = licensePlate.trim();
	const nameValue = name.trim();
	const normalizedLicensePlate = licensePlateValue.toUpperCase().replace(/-/g, "");
	const isValidPhoneValue = (value) => /^\+?[0-9]{7,15}$/.test(value);

	const getCreateError = () => {
		if (emailValue === "" && phoneValue === "" && licensePlateValue === "") {
			return t("textfield_labels.add_email_phone_or_license_plate");
		}
		if (emailValue !== "" && !isValidEmail(emailValue)) {
			return t("textfield_labels.email_invalid");
		}
		if (phoneValue !== "" && !isValidPhoneValue(phoneValue)) {
			return t("textfield_labels.phone_number_invalid");
		}
		if (licensePlateValue !== "" && !isValidLicensePlate(normalizedLicensePlate)) {
			return t("textfield_labels.license_plate_invalid");
		}
		if (nameValue !== "" && !isValidName(nameValue)) {
			return t("textfield_labels.name_invalid");
		}

		return "";
	};

	const getAccessUserPayload = () => {
		return {
			...(emailValue !== "" && { user_email: emailValue }),
			...(phoneValue !== "" && { user_phone: phoneValue }),
			...(licensePlateValue !== "" && { user_license_plate: normalizedLicensePlate }),
			...(nameValue !== "" && { user_name: nameValue })
		};
	};

	const handleStartDate = (value) => {
		// if value is null, set to current date
		if (value === null) {
			value = new Date();
			value.setHours(0, 0, 0, 0);
		}
		setStartDate(value);
	};

	const handleEndDate = (value) => {
		// if value is null, set to 2038 start
		if (value === null) {
			value = new Date("2038-01-01T00:00:00Z");
		}
		setEndDate(value);
	};

	const handleStart = (value) => {
		// if value is null, set to 00:00
		if (value === null) {
			value = new Date();
			value.setHours(0, 0, 0, 0);
		}
		setStartTime(value);
	};

	const handleEnd = (value) => {
		// if value is null, set to 00:00
		if (value === null) {
			value = new Date();
			value.setHours(0, 0, 0, 0);
		}
		setEndTime(value);
	};

	const handleWeekdays = (day) => {
		setSelectedWeekdays((prevState) => {
			// convert string into array
			const convertToArray = String(prevState).split("");

			// check if day is already in array
			const dayIndex = convertToArray.indexOf(String(day));

			if (dayIndex !== -1) {
				// if day is in array, remove it
				convertToArray.splice(dayIndex, 1);
			} else {
				// if day is not in array, add it
				convertToArray.push(String(day));
			}

			return convertToArray.join(""); // convert array back to string
		});
	};

	const handleRemove = () => {
		ApiCall("DELETE", "client/access/" + data.id)
			.then(() => {
				setError("");
				closeDialog();
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const handleSave = () => {
		setLoading(true);
		// if data is not null, we are editing an existing access right
		if (data !== null) {
			ApiCall("PUT", "client/access/" + data.id, {
				access_starts_at: startDate,
				access_ends_at: endDate,
				weekdays: selectedWeekdays,
				start_time: startTime,
				end_time: endTime,
				restrict_times: restrictions
			}).then(() => {
					setError("");
					closeDialog();
					setLoading(false);
				}).catch((error) => {
					console.log(error);
					setError(error);
					setLoading(false);
				});
		} else {
			const createError = getCreateError();
			if (createError !== "") {
				setError(createError);
				setLoading(false);
				return;
			}

			ApiCall("POST", "client/access/" + doorId, {
				...getAccessUserPayload(),
				access_starts_at: startDate,
				access_ends_at: endDate,
				weekdays: selectedWeekdays,
				start_time: startTime,
				end_time: endTime,
				restrict_times: restrictions
			}).then(() => {
					setError("");
					closeDialog();
					setLoading(false);
				}).catch((error) => {
					setError(error);
					setLoading(false);
				});
			}
	};

	return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "20px",
					padding: "20px"
				}}
			>
				<div
					style={{
						display: "inherit",
						justifyContent: "space-between",
						alignItems: "center"
					}}
				>
					<Typography
						variant="form_bold"
						fontSize={20}
						fontWeight={700}
						height={"100%"}
						lineHeight={!isDesktop && 1}
					>
						{data != null ? t("doors.edit_access_right") : t("doors.add_new_access")}
					</Typography>

					{data != null && <ConfirmAction
						background={true}
						buttonText={t("buttons.delete")}
						handleConfirmAction={() => handleRemove()}
						tooltipTitle={t("buttons.remove_user_access")}
						useTrashIcon={true}
					/>
				}
				</div>

				<Divider />

				{data == null ?
					<>
						<TextFieldInput 
							title={t("textfield_labels.email")} 
							defaultText={t("textfield_labels.input_email")}
							text={email} 
							valueChange={setEmail} 
							hasProps={
								<MailIcon
									stroke={ColorScheme.darkGrey}
									height={26}
									strokeWidth={1.5}
								/>
							}
							checkValue={emailValue !== "" && !isValidEmail(emailValue)}
						/>
						<TextFieldInput 
							title={t("textfield_labels.phone")} 
							defaultText={t("textfield_labels.input_phone")}
							text={phone} 
							valueChange={setPhone} 
							hasProps={
								<PhoneIcon
									stroke={ColorScheme.darkGrey}
									height={26}
									strokeWidth={1.5}
								/>
							}
							checkValue={phoneValue !== "" && !isValidPhoneValue(phoneValue)}
						/>
						<TextFieldInput 
							title={t("textfield_labels.license_plate")} 
							defaultText={t("textfield_labels.input_license_plate")}
							text={licensePlate} 
							valueChange={setLicensePlate} 
							hasProps={
								<CarIcon
									stroke={ColorScheme.darkGrey}
									height={26}
									strokeWidth={1.5}
								/>
							}
							checkValue={licensePlateValue !== "" && !isValidLicensePlate(normalizedLicensePlate)}
						/>
						<TextFieldInput 
							title={t("textfield_labels.name")} 
							defaultText={t("textfield_labels.input_name")}
							text={name} 
							valueChange={setName} 
							hasProps={
								<ProfileIcon
									stroke={ColorScheme.darkGrey}
									height={26}
									strokeWidth={1.5}
								/>
							}
							checkValue={nameValue !== "" && !isValidName(nameValue)}
						/>
					</>
				: <Box sx={{
					borderRadius: "4px",
					padding: "20px",
					}}>
					<Typography sx={{fontSize: "16px", fontWeight: "700"}}>
						{data?.user?.name}
					</Typography>
					<Typography sx={{fontSize: "14px", fontWeight: "700", color: ColorScheme.black}}>
						{data?.user?.email}
					</Typography>
					<Typography sx={{fontSize: "14px", fontWeight: "700", color: ColorScheme.black}}>
						{data?.user?.phone}
					</Typography>
					<Typography sx={{fontSize: "14px", fontWeight: "700", color: ColorScheme.black}}>
						{data?.user?.license_plate}
					</Typography>
				</Box>}

				<Divider />

					<div style={{display: "flex", flexDirection: "row", alignItems: "flex-start"}}>
						<Collapse in={restrictions} collapsedSize={0}>
							<AlertBar
								type="info"
								textContent={t(
									"doors.access_right_restriction_duration"
								)}
								whiteSpacePreLine
								standardVariant
								showAlert
							/>
						</Collapse>
						<div style={{width: 50}}></div>
						<OnOffSlider
							status={restrictions}
							action={setRestrictions}
							helperText={t("buttons.add_time_restriction")}
					/>
					</div>

				<Collapse in={restrictions} collapsedSize={0}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "20px",
							transition: "display 0.8s"
						}}
					>

						<Typography sx={{fontSize: "16px", fontWeight: "700"}}>
							{t("doors.restrict_by_date")}
						</Typography>

						<div
							style={{
								display: "inherit",
								flexDirection: isDesktop ? "row" : "column",
								gap: "20px"
							}}
						>
							<TextfieldPicker
								maskUnlimitedStart={false}
								value={startDate}
								excludePast={false}
								label={t("textfield_labels.start_date")}
								fullDate={true}
								valueChange={handleStartDate}
								hasProps={
									<RestrictionStartIcon
										stroke={ColorScheme.darkGrey}
									/>
								}
							/>

							{!handleWidthCheck(470) && (
								<ArrowIcon
									fill={ColorScheme.black}
									strokeWidth={0.5}
									style={{
										transform: "rotate(180deg)",
										width: "20px",
										height: "80px"
									}}
								/>
							)}

							<TextfieldPicker
								value={endDate}
								maskUnlimitedEnd={true}
								excludePast={true}
								label={t("textfield_labels.end_date")}
								fullDate={true}
								valueChange={handleEndDate}
								hasProps={
									<RestrictionEndIcon
										stroke={ColorScheme.darkGrey}
									/>
								}
							/>
						</div>

						<Typography sx={{fontSize: "16px", fontWeight: "700"}}>
							{t("doors.restrict_by_time")}
						</Typography>

						<div
							style={{
								display: "inherit",
								flexDirection: isDesktop ? "row" : "column",
								gap: "20px"
							}}
						>
							<TextfieldPicker
								value={startTime}
								isTimeRange={true}
								limitValues={true}
								limitMax={endTime}
								label={t("recipes.from_time")}
								fullDate={false}
								valueChange={handleStart}
								excludePast={false}
								hasProps={
									<RestrictionStartIcon
										stroke={ColorScheme.darkGrey}
									/>
								}
							/>

							{!handleWidthCheck(470) && (
								<ArrowIcon
								fill={ColorScheme.black}
								strokeWidth={0.5}
								style={{
									transform: "rotate(180deg)",
									width: "20px",
									height: "80px"
								}}
							/>
							)}

							<TextfieldPicker
								value={endTime}
								isTimeRange={true}
								limitValues={true}
								limitMin={startTime}
								label={t("recipes.to_time")}
								fullDate={false}
								valueChange={handleEnd}
								excludePast={false}
								hasProps={
									<RestrictionEndIcon
										stroke={ColorScheme.darkGrey}
									/>
								}
							/>
						</div>
						
						<Typography sx={{fontSize: "16px", fontWeight: "700"}}>
							{t("doors.restrict_by_weekdays")}
						</Typography>

						<WeekdaySelect
							days={String(selectedWeekdays).split("")}
							weekdayClicked={handleWeekdays}
							singleSelect={false}
						/>
					</div>
				</Collapse>

				<Collapse in={error.length > 0} collapsedSize={0}>
					<AlertBar
						type="error"
						textContent={error}
						whiteSpacePreLine
						standardVariant
						showAlert
					/>
				</Collapse>

				<Stack
					sx={{
						width: "100%",
						alignItems: "center",
						paddingTop: "15px",
						flexDirection: "row",
						justifyContent: "space-between",
						gap: "10px"
					}}
				>
					<BigButton 
						action={closeDialog}
						text={t("buttons.cancel")}
						backgroundColor={ColorScheme.buttonSecondary}
						textColor={ColorScheme.buttonSecondaryText}
						borderColor={ColorScheme.buttonPrimary}
						width={"47%"}
					/>
					<BigButton 
						action={handleSave}
						text={t("buttons.save")}
						backgroundColor={ColorScheme.buttonPrimary}
						textColor={ColorScheme.textPrimary}
						width={"47%"}
						activity={loading}
						disabled={data == null && getCreateError() !== ""}
					/>
				</Stack>
			</div>
	);
};
